import { useState, useEffect, useRef, useLayoutEffect, useCallback, forwardRef, useMemo } from 'react'
import { PlannedPost, PostedPost } from 'interfaces'
import ScrollListener from 'components/ScrollListener'
import styles from './PostsGridView.pcss'
import postStyles from './PostGridItem.pcss'
import PostGridItem, { DraggablePostGridItem } from './PostGridItem'
import { BulkPostActions, BulkPostActionsProps } from 'routes/publishing/components/BulkActionsMenus'
import { DndContext, MeasuringStrategy, useSensors, PointerSensor, useSensor, DragOverlay } from '@dnd-kit/core'
import { useDispatch, useSelector } from 'react-redux'
import { selectedProfilesSelector } from 'services/post/selectors'
import { message } from 'services/snackbar'
import { DRAG_DROP_WARNING, INVALID_DROP_MESSAGE } from '../PostsCalendarView/PostsCalendarView'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import { Observable } from 'rxjs'
import { GridScrollSeekPlaceholderProps, VirtuosoGrid } from 'react-virtuoso'
import { ScrollSeekPlaceholder } from 'components/VirtualizedList'

const SCROLL_TRESHOLD = 350
interface PostsGridViewProps {
  posts: Array<PlannedPost | PostedPost>
  selectedPosts: Record<string, boolean> | 'all'
  bulkActionsProps: BulkPostActionsProps
  withDragAndDrop: boolean
  onLoadNextPage: () => void
  onPostClick?: (post: PlannedPost) => void
  onTogglePostSelected: (postId: string) => void
  onEditPost?: (post: PlannedPost) => void
  onCopyPost: (post: PlannedPost | PostedPost) => void
  onDeletePost?: (id: string) => void
  onShareNow?: (post: PlannedPost | PostedPost) => void
  onPostOrderChanged?: (movedPost: PlannedPost, targetPost: PlannedPost) => void
  onRequeuePost?: (id: string, ppid: string) => void
  onRepost?: (post: PostedPost) => void
  onToggleFavorite?: (postId: string, ppid: string, value: boolean) => Observable<any>
  onArchive?: (postId: string, ppid: string) => void
}

export function PostsGridView(props: PostsGridViewProps) {
  const dispatch = useDispatch()
  const { posts, onPostOrderChanged } = props
  const selectedProfiles = useSelector(selectedProfilesSelector)
  const [movingPost, setMovingPost] = useState<PlannedPost | null>(null)
  const [postsMap, setPostsMap] = useState<Record<string, PlannedPost & { index: number }>>({})
  const [order, setOrder] = useState(posts.map(p => p.id))

  const selectedMap = useMemo(() => {
    if (props.selectedPosts === 'all') {
      return new Set(props.posts.map(p => p.id))
    }
    return new Set(Object.keys(props.selectedPosts).filter(id => (props.selectedPosts as Record<string, boolean>)[id]))
  }, [props.selectedPosts, props.posts])

  useEffect(() => {
    setOrder(posts.map(p => p.id))
  }, [posts])

  useEffect(() => {
    let index = 0
    const postIndex: Record<string, PlannedPost & { index: number }> = {}
    posts.forEach((post: PlannedPost) => {
      if (!post.isEmpty && !post.isPosted) {
        postIndex[post.id] = { ...post, index: index++ }
      }
    }, {})
    setPostsMap(postIndex)
  }, [posts])

  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)

  useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
  }, [])

  const onDragEnd = useCallback((event: any) => {
    if (selectedProfiles.length > 1) {
      return
    }
    const { over } = event
    const fromPost = postsMap[movingPost?.id || '']
    const toPost = postsMap[over?.data?.current?.post?.id || '']
    setMovingPost(null)
    if (!fromPost || !toPost || fromPost?.id === toPost?.id) {
      return
    }
    if (
      !toPost
      || !toPost.autoPost
      || toPost.profileId !== fromPost.profileId
      || fromPost?.bucketId !== toPost?.bucketId
    ) {
      // eslint-disable-next-line no-magic-numbers
      dispatch(message(INVALID_DROP_MESSAGE, 'warning', 5000, true))
      return
    }
    if (onPostOrderChanged) {
      onPostOrderChanged(fromPost, toPost)
    }

  }, [dispatch, movingPost?.id, onPostOrderChanged, postsMap, selectedProfiles.length])

  const onDragStart = (start: any) => {
    if (selectedProfiles.length > 1) {
      // eslint-disable-next-line no-magic-numbers
      dispatch(message(DRAG_DROP_WARNING, 'info', 5000))
    } else {
      setMovingPost(start.active?.data?.current?.post || null)
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4
      }
    })
  )

  return (
    <ScrollListener
      scrollElement={scrollElementRef.current}
      emitTreshold={SCROLL_TRESHOLD}
      onScroll={props.onLoadNextPage}
    >
      <div>
        <div className={styles.actions}>
          <BulkPostActions {...props.bulkActionsProps} />
        </div>
        <DndContext
          sensors={sensors}
          measuring={{ strategy: MeasuringStrategy.Always }}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
        >
          <SortableContext items={order} strategy={rectSortingStrategy}>
            <VirtuosoGrid
              customScrollParent={scrollElementRef.current}
              totalCount={posts.length}
              increaseViewportBy={500}
              components={{
                List: ListComponent,
                Item: ItemComponent,
                ScrollSeekPlaceholder: PostGridItemPlaceholder
              }}
              scrollSeekConfiguration={{
                /* eslint-disable no-magic-numbers */
                enter: (velocity) => Math.abs(velocity) > 800,
                exit: (velocity) => Math.abs(velocity) < 80
                /* eslint-enable no-magic-numbers */
              }}
              itemContent={(index) => {
                const post = posts[index]
                if (!post) {
                  return null
                }
                if (post.isEmpty || post.isPosted) {
                  return (
                    <div className={postStyles['post-container']} key={post.id}>
                      <PostGridItem
                        key={post.id}
                        post={post}
                        selected={selectedMap.has(post.id)}
                        className={styles['grid-item']}
                        onClick={props.onPostClick}
                        onToggleSelected={props.onTogglePostSelected}
                        onCopy={props.onCopyPost}
                        onDelete={props.onDeletePost}
                        onEdit={props.onEditPost}
                        onShareNow={props.onShareNow}
                        onRepost={props.onRepost}
                        onRequeue={props.onRequeuePost}
                        onToggleFavorite={props.onToggleFavorite}
                        onArchive={props.onArchive}
                      />
                    </div>
                  )
                }
                let dndClass: string = ''
                if (
                  !(post as PlannedPost).isVirtual
                  && post.autoPost
                  && movingPost
                  && movingPost.profileId === post.profileId
                  && movingPost.id !== post.id
                  && movingPost.bucketId === post.bucketId
                ) {
                  dndClass = postStyles['drop-target']
                }
                return (
                  <DraggablePostGridItem
                    key={post.id}
                    post={post}
                    selected={selectedMap.has(post.id)}
                    disabled={!props.withDragAndDrop}
                    className={styles['grid-item']}
                    containerClassName={dndClass}
                    onClick={props.onPostClick}
                    onToggleSelected={props.onTogglePostSelected}
                    onCopy={props.onCopyPost}
                    onDelete={props.onDeletePost}
                    onEdit={props.onEditPost}
                    onShareNow={props.onShareNow}
                  />
                )
              }}
            />
          </SortableContext>
        </DndContext>
      </div>
    </ScrollListener>
  )
}

const ListComponent = forwardRef(({ style, children }: any, ref: React.ForwardedRef<HTMLDivElement>) => (
  <div ref={ref} className={styles.grid} style={style}>
    {children}
  </div>
))

const ItemComponent = ({ children }: any) => children

const PostGridItemPlaceholder = (props: GridScrollSeekPlaceholderProps) => (
  <ScrollSeekPlaceholder
    {...props}
    type="item"
    className={postStyles.placeholder}
    containerClassName={postStyles.placeholderContainer}
  />
)

const ListRenderer = (props: any) => <ListComponent {...props} className={styles.grid} />

export default PostsGridView
